import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { initialOutlookState, IOutlookNotification, IOutlookAdvancedSearch, initialAdvancedSearch } from "./outlook.types";
import {
  fetchOutlookFoldersList,
  fetchOutlookInboxByMessageId,
  fetchOutlookFoldersEmailsList,
  fetchOutlookNotifications,
  markReadUnreadEmailInOutlook,
  markReadEmailNotification,
  outlookConnectStatus,
  deleteOutlookMessageById,
  changeMessageFlag,
  fetchOutlookLabels,
  createLabel,
  assignLabelToMessage,
  logoutOutlook,
  archiveOutlookMessageById,
  fetchCalenderEvents,
  fetchOutlookEventById,
  moveOutlookMessagesToFolder,
  batchMarkReadUnreadInOutlook,
  fetchChildFolders,
  deleteOutlookFolder,
  fetchAllInboxesEmails,
} from "./outlookThunk";
import { fetchSharedMailboxes, registerSharedMailbox, verifySharedMailbox, unregisterSharedMailbox } from "./sharedMailboxThunk";
import { LoadState } from "../../constants/enums";
import moment from "moment";

const outlookSlice = createSlice({
  name: "outlook",
  initialState: initialOutlookState,
  reducers: {
    addOutlookNotification: (
      state,
      action: PayloadAction<IOutlookNotification>,
    ) => {
      state.notifications.list.unshift(action.payload);
      state.notifications.unreadCount += 1;
    },
    setSelectedMessageIds: (state, action: PayloadAction<string[]>) => {
      state.selectedMessageIds = action.payload;
    },
    updatedSelectedFolder: (state, action) => {
      state.emails.list = [];
      state.emails.loading = LoadState.NotLoaded;
      state.selectedMessageIds = [];
      state.emailsMeta.searchNextLink = null;
      state.emailsMeta.labelFilter = "";
      state.emailsMeta.readFilter = "all";
      state.emailsMeta.flagFilter = "all";
      state.emailsMeta.advancedSearch = initialAdvancedSearch;
      state.emailsMeta.pagination.page = initialOutlookState.emailsMeta.pagination.page;
      state.emailsMeta.pagination.pageSize = initialOutlookState.emailsMeta.pagination.pageSize;
      state.selectedFolderId = action.payload;
      state.messageById = {
        messageData: null,
        loading: LoadState.NotLoaded,
      };
    },
    updateSearchQuery: (
      state,
      action: PayloadAction<{
        searchQuery: string;
      }>,
    ) => {
      const { searchQuery } = action.payload;
      state.emailsMeta.searchQuery = searchQuery;
      state.emailsMeta.searchNextLink = null;
      state.emailsMeta.pagination.page = 0;
      state.emailsMeta.pagination.pageSize = 10;
      state.emails.list = [];
      state.emails.hasMore = true;
      state.emails.loading = LoadState.NotLoaded;
    },
    updateLabelFilter: (
      state,
      action: PayloadAction<{ label: string }>,
    ) => {
      const { label } = action.payload;
      state.emailsMeta.labelFilter = label;
      state.emailsMeta.searchNextLink = null;
      state.emailsMeta.pagination.page = 0;
      state.emailsMeta.pagination.pageSize = 20;
      state.emails.list = [];
      state.emails.hasMore = true;
      state.emails.loading = LoadState.NotLoaded;
    },
    updateReadFilter: (
      state,
      action: PayloadAction<{ readFilter: "all" | "read" | "unread" }>,
    ) => {
      const { readFilter } = action.payload;
      state.emailsMeta.readFilter = readFilter;
      state.emailsMeta.searchNextLink = null;
      state.emailsMeta.pagination.page = 0;
      state.emailsMeta.pagination.pageSize = 20;
      state.emails.list = [];
      state.emails.hasMore = true;
      state.emails.loading = LoadState.NotLoaded;
    },
    updateFlagFilter: (
      state,
      action: PayloadAction<{ flagFilter: "all" | "notFlagged" | "flagged" | "complete" }>,
    ) => {
      const { flagFilter } = action.payload;
      state.emailsMeta.flagFilter = flagFilter;
      state.emailsMeta.searchNextLink = null;
      state.emailsMeta.pagination.page = 0;
      state.emailsMeta.pagination.pageSize = 20;
      state.emails.list = [];
      state.emails.hasMore = true;
      state.emails.loading = LoadState.NotLoaded;
    },
    updateAdvancedSearch: (
      state,
      action: PayloadAction<{ advancedSearch: IOutlookAdvancedSearch }>,
    ) => {
      const { advancedSearch } = action.payload;
      state.emailsMeta.advancedSearch = advancedSearch;
      state.emailsMeta.searchNextLink = null;
      state.emailsMeta.pagination.page = 0;
      state.emailsMeta.pagination.pageSize = 10;
      state.emails.list = [];
      state.emails.hasMore = true;
      state.emails.loading = LoadState.NotLoaded;
    },
    clearAdvancedSearch: (state) => {
      state.emailsMeta.advancedSearch = initialAdvancedSearch;
      state.emailsMeta.searchNextLink = null;
      state.emailsMeta.pagination.page = 0;
      state.emailsMeta.pagination.pageSize = 20;
      state.emails.list = [];
      state.emails.hasMore = true;
      state.emails.loading = LoadState.NotLoaded;
    },
    updateEmailPagination: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>,
    ) => {
      const { page, pageSize } = action.payload;
      state.emailsMeta.pagination.page = page;
      state.emailsMeta.pagination.pageSize = pageSize;
    },
    disconnectOutlook: (state) => {
      state.connection.connected = false;
      state.connection.email = "";
      state.connection.expiresAt = "";
      state.connection.isExpired = false;
    },
    setActiveMailbox: (state, action: PayloadAction<string | null>) => {
      state.activeMailbox = action.payload;
      // Reset emails
      state.emails.list = [];
      state.emails.loading = LoadState.NotLoaded;
      state.emails.hasMore = true;
      // Reset folders
      state.folders.list = [];
      state.folders.loading = LoadState.NotLoaded;
      // Reset selected folder
      state.selectedFolderId = null;
      // Reset message by id
      state.messageById = {
        messageData: null,
        loading: LoadState.NotLoaded,
      };
      // Reset emailsMeta to initial pagination/filter values
      state.emailsMeta = {
        searchQuery: "",
        searchNextLink: null,
        labelFilter: "",
        readFilter: "all",
        flagFilter: "all",
        advancedSearch: initialAdvancedSearch,
        pagination: {
          page: 1,
          pageSize: 50,
        },
      };
      // Reset selected message ids
      state.selectedMessageIds = [];
      // If switching to a shared mailbox, reset its unread count
      if (action.payload !== null) {
        state.mailboxUnreadCounts[action.payload] = 0;
      }
    },
    incrementMailboxUnread: (state, action: PayloadAction<string>) => {
      const mailboxKey = action.payload;
      if (state.mailboxUnreadCounts[mailboxKey]) {
        state.mailboxUnreadCounts[mailboxKey] += 1;
      } else {
        state.mailboxUnreadCounts[mailboxKey] = 1;
      }
    },
    resetMailboxUnread: (state, action: PayloadAction<string>) => {
      state.mailboxUnreadCounts[action.payload] = 0;
    },
    clearVerifyResult: (state) => {
      state.sharedMailboxes.verifyResult = null;
    },
    invalidateChildFolders: (state, action: PayloadAction<string>) => {
      delete state.childFolders[action.payload];
    },
    clearOutlookState: () => {
      return initialOutlookState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(outlookConnectStatus.pending, (state) => {
        state.connection.connected = false;
        state.connection.email = "";
        state.connection.expiresAt = "";
        state.connection.isExpired = false;
        state.connection.loading = LoadState.InProgress;
      })
      .addCase(outlookConnectStatus.fulfilled, (state, action) => {
        const { connected, email, expiresAt, isExpired } = action.payload;

        const nowUTC = moment.utc();
        const expiryUTC = moment.utc(expiresAt);

        console.log("Now (UTC):", nowUTC.format());
        console.log("Expiry (UTC):", expiryUTC.format());

        const isDateExpired = nowUTC.isAfter(expiryUTC);

        state.connection.connected = isDateExpired ? false : connected;
        state.connection.email = email;
        state.connection.expiresAt = expiresAt;
        state.connection.isExpired = isExpired;
        state.connection.loading = LoadState.Loaded;
      })
      .addCase(outlookConnectStatus.rejected, (state) => {
        state.connection.connected = false;
        state.connection.email = "";
        state.connection.expiresAt = "";
        state.connection.isExpired = false;
        state.connection.loading = LoadState.Failed;
      })
      .addCase(fetchOutlookFoldersList.pending, (state) => {
        state.folders.loading = LoadState.InProgress;
      })
      .addCase(fetchOutlookFoldersList.fulfilled, (state, action) => {
        const { folders } = action.payload;
        state.folders.list = [...folders];
        if (folders.length > 0) {
          const inboxFolder = folders.find(
            (folder) => folder.displayName.toLowerCase() === "inbox",
          );
          state.selectedFolderId = inboxFolder ? inboxFolder.id : folders[0].id;
        }
        state.folders.loading = LoadState.Loaded;
      })
      .addCase(fetchOutlookFoldersList.rejected, (state) => {
        state.folders.list = [];
        state.folders.loading = LoadState.Failed;
      })
      .addCase(fetchOutlookFoldersEmailsList.pending, (state) => {
        state.emails.loading = LoadState.InProgress;
        state.emails.hasMore = false;
      })
      .addCase(fetchOutlookFoldersEmailsList.fulfilled, (state, action) => {
        const { emailList, hasMore, nextLink } = action.payload;
        state.emails.list = [...state.emails.list, ...emailList];
        state.emails.hasMore = hasMore;
        state.emails.loading = LoadState.Loaded;
        state.emailsMeta.searchNextLink = nextLink;
      })
      .addCase(fetchOutlookFoldersEmailsList.rejected, (state) => {
        state.emails.hasMore = false;
        state.emails.loading = LoadState.Failed;
      })
      .addCase(fetchAllInboxesEmails.pending, (state) => {
        state.emails.loading = LoadState.InProgress;
        state.emails.hasMore = false;
      })
      .addCase(fetchAllInboxesEmails.fulfilled, (state, action) => {
        const { emailList, hasMore, nextLink } = action.payload;
        state.emails.list = [...state.emails.list, ...emailList];
        state.emails.hasMore = hasMore;
        state.emails.loading = LoadState.Loaded;
        state.emailsMeta.searchNextLink = nextLink;
      })
      .addCase(fetchAllInboxesEmails.rejected, (state) => {
        state.emails.hasMore = false;
        state.emails.loading = LoadState.Failed;
      })
      .addCase(fetchOutlookInboxByMessageId.pending, (state) => {
        state.messageById.loading = LoadState.InProgress;
      })
      .addCase(fetchOutlookInboxByMessageId.fulfilled, (state, action) => {
        const { message } = action.payload;
        state.messageById.messageData = message;
        state.messageById.loading = LoadState.Loaded;
      })
      .addCase(fetchOutlookInboxByMessageId.rejected, (state) => {
        state.messageById.loading = LoadState.Failed;
      })
      .addCase(fetchOutlookNotifications.pending, (state) => {
        state.notifications.loading = LoadState.InProgress;
      })
      .addCase(fetchOutlookNotifications.fulfilled, (state, action) => {
        const { notifications } = action.payload;
        if (notifications.length === 0) {
          state.notifications.fetchingMore = false;
        }
        state.notifications.list = [
          ...state.notifications.list,
          ...notifications,
        ];
        state.notifications.unreadCount = state.notifications.list.filter(
          (n) => n.read_status !== "READ",
        ).length;
        state.notifications.loading = LoadState.Loaded;
      })
      .addCase(fetchOutlookNotifications.rejected, (state) => {
        state.notifications.list = [];
        state.notifications.loading = LoadState.Failed;
      })
      .addCase(markReadEmailNotification.fulfilled, (state, action) => {
        const { messageId } = action.payload;
        const notification = state.notifications.list.find(
          (n) => n.message_id === messageId,
        );
        if (notification && notification.read_status !== "READ") {
          notification.read_status = "READ";
          state.notifications.unreadCount -= 1;
        }
      })
      .addCase(markReadUnreadEmailInOutlook.fulfilled, (state, action) => {
        const { messageId, isRead } = action.payload;
        const folderId = state.selectedFolderId;
        if (state.messageById.messageData?.id === messageId) {
          state.messageById.messageData.isRead = isRead;
        }
        const emailIndex = state.emails.list.findIndex(
          (email) => email.id === messageId,
        );
        if (emailIndex !== -1) {
          state.emails.list[emailIndex].isRead = isRead;
        }
        if (folderId) {
          const folderIndex = state.folders.list.findIndex(
            (folder) => folder.id === folderId,
          );
          if (folderIndex !== -1) {
            if (isRead) {
              state.folders.list[folderIndex].unreadItemCount -= 1;
            } else {
              state.folders.list[folderIndex].unreadItemCount += 1;
            }
          }
        }
      })
      .addCase(deleteOutlookMessageById.fulfilled, (state, action) => {
        const { messageIds } = action.payload;
        if (
          state.messageById.messageData &&
          messageIds.includes(state.messageById.messageData.id)
        ) {
          state.messageById.messageData = null;
          state.messageById.loading = LoadState.NotLoaded;
        }
        state.emails.list = state.emails.list.filter(
          (email) => !messageIds.includes(email.id),
        );
        const selectedIndex = state.selectedMessageIds.findIndex((id) =>
          messageIds.includes(id),
        );
        if (selectedIndex !== -1) {
          state.selectedMessageIds.splice(selectedIndex, 1);
        }
        //state.emails.totalRecords -= messageIds.length;
      })
      .addCase(archiveOutlookMessageById.fulfilled, (state, action) => {
        const { messageIds } = action.payload;
        if (
          state.messageById.messageData &&
          messageIds.includes(state.messageById.messageData.id)
        ) {
          state.messageById.messageData = null;
          state.messageById.loading = LoadState.NotLoaded;
        }
        state.emails.list = state.emails.list.filter(
          (email) => !messageIds.includes(email.id),
        );
        const selectedIndex = state.selectedMessageIds.findIndex((id) =>
          messageIds.includes(id),
        );
        if (selectedIndex !== -1) {
          state.selectedMessageIds.splice(selectedIndex, 1);
        }
        //state.emails.totalRecords -= messageIds.length;
      })
      .addCase(moveOutlookMessagesToFolder.fulfilled, (state, action) => {
        const { messageIds } = action.payload;
        if (
          state.messageById.messageData &&
          messageIds.includes(state.messageById.messageData.id)
        ) {
          state.messageById.messageData = null;
          state.messageById.loading = LoadState.NotLoaded;
        }
        state.emails.list = state.emails.list.filter(
          (email) => !messageIds.includes(email.id),
        );
        state.selectedMessageIds = state.selectedMessageIds.filter(
          (id) => !messageIds.includes(id),
        );
      })
      .addCase(batchMarkReadUnreadInOutlook.fulfilled, (state, action) => {
        const { messageIds, isRead } = action.payload;
        const folderId = state.selectedFolderId;
        let delta = 0;

        state.emails.list.forEach((email) => {
          if (messageIds.includes(email.id)) {
            if (email.isRead !== isRead) {
              if (isRead) {
                delta -= 1;
              } else {
                delta += 1;
              }
              email.isRead = isRead;
            }
          }
        });

        if (
          state.messageById.messageData?.id &&
          messageIds.includes(state.messageById.messageData.id)
        ) {
          state.messageById.messageData.isRead = isRead;
        }

        if (folderId) {
          const folderIndex = state.folders.list.findIndex(
            (folder) => folder.id === folderId,
          );
          if (folderIndex !== -1) {
            state.folders.list[folderIndex].unreadItemCount += delta;
          }
        }

        state.selectedMessageIds = [];
      })
      .addCase(changeMessageFlag.fulfilled, (state, action) => {
        const { messageId, flag, dueDateTime } = action.payload;
        if (state.messageById.messageData?.id === messageId) {
          state.messageById.messageData.flag.flagStatus = flag;
          // if (dueDateTime) {
          //   state.messageById.messageData.flag.flagDueDateTime = dueDateTime;
          // }
        }
        const emailIndex = state.emails.list.findIndex(
          (email) => email.id === messageId,
        );
        if (emailIndex !== -1) {
          state.emails.list[emailIndex].flag.flagStatus = flag;
          // if (dueDateTime) {
          //   state.emails.list[emailIndex].flagDueDateTime = dueDateTime;
          // }
        }
      })
      .addCase(fetchOutlookLabels.pending, (state) => {
        state.labels.loading = LoadState.InProgress;
        state.labels.list = [];
      })
      .addCase(fetchOutlookLabels.fulfilled, (state, action) => {
        const { labels } = action.payload;
        state.labels.list = [...labels];
        state.labels.loading = LoadState.Loaded;
      })
      .addCase(fetchOutlookLabels.rejected, (state) => {
        state.labels.list = [];
        state.labels.loading = LoadState.Failed;
      })

      .addCase(createLabel.fulfilled, (state, action) => {
        const { label } = action.payload;
        state.labels.list.push(label);
      })

      .addCase(assignLabelToMessage.fulfilled, (state, action) => {
        const { messageId, data } = action.payload;
        if (state.messageById.messageData?.id === messageId) {
          state.messageById.messageData.categories = data;
        }
        const emailIndex = state.emails.list.findIndex(
          (email) => email.id === messageId,
        );
        if (emailIndex !== -1) {
          state.emails.list[emailIndex].categories = data;
        }
      })
      .addCase(logoutOutlook.fulfilled, (state) => {
        state.connection.connected = false;
        state.connection.email = "";
        state.connection.expiresAt = "";
        state.connection.isExpired = false;
      })
      .addCase(fetchCalenderEvents.pending, (state) => {
        state.calenderEvents.loading = LoadState.InProgress;
        state.calenderEvents.list = [];
      })
      .addCase(fetchCalenderEvents.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.calenderEvents.list = [...data];
        state.calenderEvents.loading = LoadState.Loaded;
      })
      .addCase(fetchCalenderEvents.rejected, (state) => {
        state.calenderEvents.list = [];
        state.calenderEvents.loading = LoadState.Failed;
      })
      .addCase(fetchOutlookEventById.pending, (state) => {
        state.calenderEvents.eventDataByIdLoading = LoadState.InProgress;
        state.calenderEvents.eventDataById = initialOutlookState.calenderEvents.eventDataById;
      })
      .addCase(fetchOutlookEventById.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.calenderEvents.eventDataById = data;
        state.calenderEvents.eventDataByIdLoading = LoadState.Loaded;
      })
      .addCase(fetchOutlookEventById.rejected, (state) => {
        state.calenderEvents.eventDataById = initialOutlookState.calenderEvents.eventDataById;
        state.calenderEvents.eventDataByIdLoading = LoadState.Failed;
      })
      .addCase(fetchChildFolders.pending, (state, action) => {
        const folderId = action.meta.arg.folderId;
        state.childFolders[folderId] = {
          list: state.childFolders[folderId]?.list || [],
          loading: LoadState.InProgress,
        };
      })
      .addCase(fetchChildFolders.fulfilled, (state, action) => {
        const { parentFolderId, folders } = action.payload;
        state.childFolders[parentFolderId] = {
          list: folders,
          loading: LoadState.Loaded,
        };
      })
      .addCase(fetchChildFolders.rejected, (state, action) => {
        const folderId = action.meta.arg.folderId;
        state.childFolders[folderId] = {
          list: state.childFolders[folderId]?.list || [],
          loading: LoadState.Failed,
        };
      })
      .addCase(deleteOutlookFolder.fulfilled, (state, action) => {
        const { folderId } = action.payload;
        for (const parentId of Object.keys(state.childFolders)) {
          state.childFolders[parentId].list = state.childFolders[parentId].list.filter(
            (folder) => folder.id !== folderId,
          );
        }
      })
      .addCase(fetchSharedMailboxes.pending, (state) => {
        state.sharedMailboxes.loading = LoadState.InProgress;
      })
      .addCase(fetchSharedMailboxes.fulfilled, (state, action) => {
        state.sharedMailboxes.list = action.payload.list;
        state.sharedMailboxes.loading = LoadState.Loaded;
      })
      .addCase(fetchSharedMailboxes.rejected, (state) => {
        state.sharedMailboxes.loading = LoadState.Failed;
      })
      .addCase(registerSharedMailbox.pending, (state) => {
        state.sharedMailboxes.registerLoading = LoadState.InProgress;
      })
      .addCase(registerSharedMailbox.fulfilled, (state, action) => {
        state.sharedMailboxes.list.push(action.payload);
        state.sharedMailboxes.registerLoading = LoadState.Loaded;
      })
      .addCase(registerSharedMailbox.rejected, (state) => {
        state.sharedMailboxes.registerLoading = LoadState.Failed;
      })
      .addCase(verifySharedMailbox.pending, (state) => {
        state.sharedMailboxes.verifyLoading = LoadState.InProgress;
        state.sharedMailboxes.verifyResult = null;
      })
      .addCase(verifySharedMailbox.fulfilled, (state, action) => {
        state.sharedMailboxes.verifyResult = action.payload;
        state.sharedMailboxes.verifyLoading = LoadState.Loaded;
      })
      .addCase(verifySharedMailbox.rejected, (state) => {
        state.sharedMailboxes.verifyLoading = LoadState.Failed;
      })
      .addCase(unregisterSharedMailbox.pending, (state) => {
        state.sharedMailboxes.unregisterLoading = LoadState.InProgress;
      })
      .addCase(unregisterSharedMailbox.fulfilled, (state, action) => {
        state.sharedMailboxes.list = state.sharedMailboxes.list.filter(
          (mailbox) => mailbox.shared_mailbox_email !== action.payload.email,
        );
        if (state.activeMailbox === action.payload.email) {
          state.activeMailbox = null;
        }
        state.sharedMailboxes.unregisterLoading = LoadState.Loaded;
      })
      .addCase(unregisterSharedMailbox.rejected, (state) => {
        state.sharedMailboxes.unregisterLoading = LoadState.Failed;
      });
  },
});

export const {
  updatedSelectedFolder,
  setSelectedMessageIds,
  addOutlookNotification,
  updateSearchQuery,
  updateLabelFilter,
  updateReadFilter,
  updateFlagFilter,
  updateAdvancedSearch,
  clearAdvancedSearch,
  updateEmailPagination,
  setActiveMailbox,
  incrementMailboxUnread,
  resetMailboxUnread,
  clearVerifyResult,
  clearOutlookState,
  disconnectOutlook,
  invalidateChildFolders,
} = outlookSlice.actions;

export const outlookReducer = outlookSlice.reducer;
